import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import { AnyAction } from "@nova/constants"

export enum VideoResolution {
    Low = "Low",
    Medium = "Med",
    High = "High",
}

export type VideoMetadata = [
    Longitude: number,
    Latitude: number,
    RelAltitude: number,
    Pitch: number,
    Yaw: number,
    Roll: number,
    // will be the focalLength for old videos/images, new ones should have the [fovX, fovY] type
    FOV: number[] | number,
    Altitude: number,
    HasLRF?: boolean,
][]

export type VideoSlice = {
    resolution: VideoResolution
    time: number
    paused: boolean
    currentVideo: string
    dataUpdated: string
    crosshairEnabled: boolean
    frameOutlineEnabled: boolean
    loading: boolean
    flightPathEnabled: boolean
    droneIconEnabled: boolean
}

const defaultVideoSliceName = "video-slice-v2"

const initialState = {
    resolution: VideoResolution.Medium,
    time: 0,
    crosshairEnabled: true,
    frameOutlineEnabled: true,
    loading: false,
    flightPathEnabled: true,
    droneIconEnabled: true,
} as VideoSlice

export const createVideoSlice = (name: string) => {
    const slice = createSlice({
        name,
        initialState: { ...initialState },
        reducers: {
            setResolution: (state, { payload }: PayloadAction<VideoResolution>) => {
                state.resolution = payload
            },
            setVideoTime: (state, { payload }: PayloadAction<number>) => {
                state.time = payload
            },
            setPaused: (state, { payload }: PayloadAction<boolean>) => {
                state.paused = payload
            },
            setCurrentVideo: (state, { payload }: PayloadAction<string>) => {
                state.currentVideo = payload
            },
            dataUpdated: (state) => {
                state.dataUpdated = new Date().toISOString()
            },
            setCrosshairEnabled: (state, { payload }: PayloadAction<boolean>) => {
                state.crosshairEnabled = payload
            },
            setFrameOutlineEnabled: (state, { payload }: PayloadAction<boolean>) => {
                state.frameOutlineEnabled = payload
            },
            setLoading: (state, { payload }: PayloadAction<boolean>) => {
                state.loading = payload
            },
            setFlightPathEnabled: (state, { payload }: PayloadAction<boolean>) => {
                state.flightPathEnabled = payload
            },
            setDroneIconEnabled: (state, { payload }: PayloadAction<boolean>) => {
                state.droneIconEnabled = payload
            },
            resetVideoState: () => {
                return { ...initialState }
            },
        },
    })
    const { actions, reducer } = slice
    const selectors = withRoot((r: AnyAction) => r[name], {
        selectResolution: () => (s) => s.resolution,
        selectCurrentVideo: () => (s) => s.currentVideo,
        selectDataUpdatedTimestamp: () => (s) => s.dataUpdated,
        selectCrosshairEnabled: () => (s) => s.crosshairEnabled,
        selectFrameOutlineEnabled: () => (s) => s.frameOutlineEnabled,
        selectLoading: () => (s) => s.loading,
        selectFlightPathEnabled: () => (s) => s.flightPathEnabled,
        selectDroneIconEnabled: () => (s) => s.droneIconEnabled,
    })
    return { actions, reducer, selectors, slice }
}

const {
    actions: {
        setResolution,
        setPaused,
        setVideoTime,
        setCurrentVideo,
        setCrosshairEnabled,
        setFrameOutlineEnabled,
        resetVideoState,
        dataUpdated,
        setLoading,
        setDroneIconEnabled,
        setFlightPathEnabled,
    },
    reducer: videoReducer,
    selectors: {
        selectDataUpdatedTimestamp,
        selectCrosshairEnabled,
        selectFrameOutlineEnabled,
        selectResolution,
        selectCurrentVideo,
        selectLoading,
        selectDroneIconEnabled,
        selectFlightPathEnabled,
    },
} = createVideoSlice(defaultVideoSliceName)

export {
    setLoading,
    setResolution,
    setPaused,
    setVideoTime,
    setCurrentVideo,
    setCrosshairEnabled,
    setFrameOutlineEnabled,
    resetVideoState,
    dataUpdated,
    selectDataUpdatedTimestamp,
    selectCrosshairEnabled,
    selectFrameOutlineEnabled,
    selectLoading,
    selectDroneIconEnabled,
    selectFlightPathEnabled,
    setDroneIconEnabled,
    setFlightPathEnabled,
}
export { selectResolution, selectCurrentVideo }
export const reducer = { [defaultVideoSliceName]: videoReducer }
